import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import PatternHintsBanner from '@/components/PatternHintsBanner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Brain, AlertTriangle, Check, Clock, Activity, Users,
  ChevronRight, MessageSquare, Loader2, Calendar, BarChart3,
  Plus, X, Play, Edit3, Trash2, Target, BookOpen
} from 'lucide-react';

const STATE_COLORS = {
  stable: '#4E7A6B', unstable: '#D4A843', underdrive: '#5C6B8A', overdrive: '#C87B7B',
  triggered: '#8C6B55', stressed: '#9B7BC8', recovery_needed: '#C85C5C',
};

// ═══════════════════════════════════════════════════════════════
// SESSION EDITOR MODAL
// ═══════════════════════════════════════════════════════════════

function SessionEditorModal({ session, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', description: '', objective: '', type: 'daily_regulation', mode: 'self',
    estimated_duration_minutes: 5, recommended_for_states: [], recommended_for_patterns: [],
    session_steps: [{ step_order: 1, title: '', instruction_text: '', breathing_count: '', body_cue: '', timing_seconds: 30 }],
    completion_reflection_prompt: '', after_effect_prompt: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (session) setForm({
      title: session.title || '', description: session.description || '', objective: session.objective || '',
      type: session.type || 'daily_regulation', mode: session.mode || 'self',
      estimated_duration_minutes: session.estimated_duration_minutes || 5,
      recommended_for_states: session.recommended_for_states || [],
      recommended_for_patterns: session.recommended_for_patterns || [],
      session_steps: session.session_steps?.length ? session.session_steps : [{ step_order: 1, title: '', instruction_text: '', breathing_count: '', body_cue: '', timing_seconds: 30 }],
      completion_reflection_prompt: session.completion_reflection_prompt || '',
      after_effect_prompt: session.after_effect_prompt || '',
    });
  }, [session]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const updateStep = (i, field, val) => setForm(p => {
    const steps = [...p.session_steps]; steps[i] = { ...steps[i], [field]: val }; return { ...p, session_steps: steps };
  });
  const addStep = () => setForm(p => ({ ...p, session_steps: [...p.session_steps, { step_order: p.session_steps.length + 1, title: '', instruction_text: '', breathing_count: '', body_cue: '', timing_seconds: 30 }] }));
  const removeStep = (i) => setForm(p => ({ ...p, session_steps: p.session_steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step_order: idx + 1 })) }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (session?.id) {
        await axios.put(`${API}/mindflo/admin/session-template/${session.id}`, form);
      } else {
        await axios.post(`${API}/mindflo/admin/session-template`, form);
      }
      toast.success('Session saved');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-adm-surface border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto p-0" data-testid="session-editor">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/[0.06]">
          <DialogTitle className="text-adm-primary font-['Work_Sans'] text-base">{session ? 'Edit Session' : 'New Session Template'}</DialogTitle>
        </DialogHeader>
        <div className="px-5 py-4 space-y-3">
          <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Session title" className="bg-adm-panel border-white/10 text-adm-primary h-9 text-sm" data-testid="session-title-input" />
          <Textarea value={form.objective} onChange={e => set('objective', e.target.value)} placeholder="Objective" className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[40px]" />
          <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Description" className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[40px]" />
          <div className="grid grid-cols-3 gap-2">
            <Select value={form.type} onValueChange={v => set('type', v)}>
              <SelectTrigger className="bg-adm-panel border-white/10 h-8 text-xs text-adm-primary"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-adm-panel border-white/10">
                {['immediate_reset', 'daily_regulation', 'trigger_rewiring', 'guided_terra_session'].map(t => <SelectItem key={t} value={t} className="text-xs">{t.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.mode} onValueChange={v => set('mode', v)}>
              <SelectTrigger className="bg-adm-panel border-white/10 h-8 text-xs text-adm-primary"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-adm-panel border-white/10">
                <SelectItem value="self" className="text-xs">Self</SelectItem>
                <SelectItem value="guided" className="text-xs">Guided</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" value={form.estimated_duration_minutes} onChange={e => set('estimated_duration_minutes', parseInt(e.target.value) || 5)} className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" placeholder="Duration (min)" />
          </div>
          {/* Steps */}
          <p className="text-[10px] text-adm-tertiary uppercase tracking-wider mt-2">Session Steps ({form.session_steps.length})</p>
          {form.session_steps.map((s, i) => (
            <div key={i} className="p-2.5 rounded-lg bg-white/[0.02] space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-adm-tertiary w-4">{i + 1}</span>
                <Input value={s.title} onChange={e => updateStep(i, 'title', e.target.value)} placeholder="Step title" className="bg-adm-panel border-white/10 text-adm-primary h-7 text-xs flex-1" />
                {form.session_steps.length > 1 && <button onClick={() => removeStep(i)} className="p-1 text-adm-tertiary hover:text-rose-400"><X className="w-3 h-3" /></button>}
              </div>
              <Textarea value={s.instruction_text} onChange={e => updateStep(i, 'instruction_text', e.target.value)} placeholder="Instruction text (the main guidance for this step)" className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[50px]" />
              <div className="grid grid-cols-3 gap-2">
                <Input value={s.breathing_count || ''} onChange={e => updateStep(i, 'breathing_count', e.target.value)} placeholder="Breathing (e.g. 4-2-6 x 4)" className="bg-adm-panel border-white/10 text-adm-primary h-7 text-xs" />
                <Input value={s.body_cue || ''} onChange={e => updateStep(i, 'body_cue', e.target.value)} placeholder="Body cue" className="bg-adm-panel border-white/10 text-adm-primary h-7 text-xs" />
                <Input type="number" value={s.timing_seconds || ''} onChange={e => updateStep(i, 'timing_seconds', parseInt(e.target.value) || 0)} placeholder="Seconds" className="bg-adm-panel border-white/10 text-adm-primary h-7 text-xs" />
              </div>
            </div>
          ))}
          <button onClick={addStep} className="text-[10px] text-adm-accent">+ Add step</button>
          <Input value={form.completion_reflection_prompt} onChange={e => set('completion_reflection_prompt', e.target.value)} placeholder="Completion reflection prompt" className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" />
          <Input value={form.after_effect_prompt} onChange={e => set('after_effect_prompt', e.target.value)} placeholder="After-effect prompt" className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" />
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/[0.06]">
          <Button variant="ghost" onClick={onClose} className="text-adm-tertiary text-xs">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border-0 text-xs" data-testid="save-session-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Session'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRIGGER EDITOR MODAL
// ═══════════════════════════════════════════════════════════════

const TRIGGER_CATS = ['neurological', 'medication', 'movement', 'emotional', 'environmental', 'cognitive', 'social', 'fatigue', 'pain', 'sleep', 'digestive'];

function TriggerEditorModal({ trigger, personId, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', category: 'neurological', description: '', body_response: '',
    thought_pattern: '', emotional_response: '', usual_behavior: '',
    desired_corrected_response: '', linked_patterns: [], linked_session_slugs: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (trigger) setForm({
      title: trigger.title || '', category: trigger.category || 'neurological',
      description: trigger.description || '', body_response: trigger.body_response || '',
      thought_pattern: trigger.thought_pattern || '', emotional_response: trigger.emotional_response || '',
      usual_behavior: trigger.usual_behavior || '', desired_corrected_response: trigger.desired_corrected_response || '',
      linked_patterns: trigger.linked_patterns || [], linked_session_slugs: trigger.linked_session_slugs || [],
    });
  }, [trigger]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (trigger?.id) {
        await axios.put(`${API}/mindflo/admin/trigger-profile/${trigger.id}`, form);
      } else {
        await axios.post(`${API}/mindflo/admin/trigger-profile`, { ...form, person_id: personId });
      }
      toast.success('Trigger saved');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-adm-surface border-white/10 max-w-lg max-h-[85vh] overflow-y-auto p-0" data-testid="trigger-editor">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/[0.06]">
          <DialogTitle className="text-adm-primary font-['Work_Sans'] text-base">{trigger ? 'Edit Trigger' : 'New Trigger Profile'}</DialogTitle>
        </DialogHeader>
        <div className="px-5 py-4 space-y-3">
          <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Trigger title" className="bg-adm-panel border-white/10 text-adm-primary h-9 text-sm" data-testid="trigger-title-input" />
          <Select value={form.category} onValueChange={v => set('category', v)}>
            <SelectTrigger className="bg-adm-panel border-white/10 h-8 text-xs text-adm-primary"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">{TRIGGER_CATS.map(c => <SelectItem key={c} value={c} className="text-xs capitalize">{c}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Description — what this trigger looks like" className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[40px]" />
          <Input value={form.body_response} onChange={e => set('body_response', e.target.value)} placeholder="Body response" className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" />
          <Input value={form.thought_pattern} onChange={e => set('thought_pattern', e.target.value)} placeholder="Thought pattern" className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" />
          <Input value={form.emotional_response} onChange={e => set('emotional_response', e.target.value)} placeholder="Emotional response" className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" />
          <Input value={form.usual_behavior} onChange={e => set('usual_behavior', e.target.value)} placeholder="Usual behavior (what they typically do)" className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs" />
          <Textarea value={form.desired_corrected_response} onChange={e => set('desired_corrected_response', e.target.value)} placeholder="Desired corrected response (what we want to rewire toward)" className="bg-adm-panel border-white/10 text-adm-primary text-xs min-h-[40px]" />
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/[0.06]">
          <Button variant="ghost" onClick={onClose} className="text-adm-tertiary text-xs">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border-0 text-xs" data-testid="save-trigger-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Trigger'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminMindFloReview() {
  const [overview, setOverview] = useState(null);
  const [selectedSub, setSelectedSub] = useState('');
  const [viewMode, setViewMode] = useState('day');
  const [adminTab, setAdminTab] = useState('overview'); // overview | sessions | triggers
  const [dayReview, setDayReview] = useState(null);
  const [weeklyReview, setWeeklyReview] = useState(null);
  const [reviewDate, setReviewDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [sessionTemplates, setSessionTemplates] = useState([]);
  const [subTriggers, setSubTriggers] = useState([]);
  const [showSessionEditor, setShowSessionEditor] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showTriggerEditor, setShowTriggerEditor] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    axios.get(`${API}/mindflo/admin/overview`).then(r => setOverview(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/mindflo/admin/session-templates`).then(r => setSessionTemplates(r.data || [])).catch(() => {});
    axios.get(`${API}/mindflo/admin/predictive-watchlist`).then(r => setWatchlist(r.data?.watchlist || [])).catch(() => {});
  }, []);

  const fetchDayReview = useCallback(async () => {
    if (!selectedSub) return;
    try {
      const res = await axios.get(`${API}/mindflo/admin/day-review/${selectedSub}?date=${reviewDate}`);
      setDayReview(res.data);
    } catch { setDayReview(null); }
  }, [selectedSub, reviewDate]);

  const fetchWeeklyReview = useCallback(async () => {
    if (!selectedSub) return;
    try {
      const res = await axios.get(`${API}/mindflo/admin/weekly-review/${selectedSub}`);
      setWeeklyReview(res.data);
    } catch { setWeeklyReview(null); }
  }, [selectedSub]);

  useEffect(() => {
    if (viewMode === 'day') fetchDayReview();
    else fetchWeeklyReview();
  }, [viewMode, fetchDayReview, fetchWeeklyReview]);

  useEffect(() => {
    if (selectedSub && adminTab === 'triggers') {
      axios.get(`${API}/mindflo/admin/trigger-profiles/${selectedSub}`).then(r => setSubTriggers(r.data?.triggers || [])).catch(() => setSubTriggers([]));
    }
  }, [selectedSub, adminTab]);

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedSub) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/mindflo/admin/note`, { person_id: selectedSub, note_text: noteText, note_type: 'observation' });
      toast.success('Note saved');
      setNoteText('');
      fetchDayReview();
    } catch { toast.error('Failed to save note'); }
    finally { setSavingNote(false); }
  };

  const selectedSubInfo = overview?.subscribers?.find(s => s.subscriber_id === selectedSub);
  const { user: currentUser } = useAuth();

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6" data-testid="admin-mindflo">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">MindFl&#x14D; Dashboard</h1>
            <p className="text-xs text-adm-tertiary mt-1">Neuro-behavioral state intelligence review</p>
          </div>
          <div className="flex gap-1">
            {[
              { key: 'overview', label: 'Overview', icon: Brain },
              { key: 'sessions', label: 'Session Library', icon: Play },
              { key: 'triggers', label: 'Trigger Builder', icon: Target },
            ].map(t => (
              <button key={t.key} onClick={() => setAdminTab(t.key)}
                className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors ${adminTab === t.key ? 'bg-adm-accent/15 text-adm-accent' : 'text-adm-tertiary hover:text-adm-secondary'}`}
                data-testid={`admin-tab-${t.key}`}>
                <t.icon className="w-3 h-3" strokeWidth={1.5} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div>
        ) : adminTab === 'overview' ? (
          <>
            {/* Predictive Watchlist */}
            {watchlist.length > 0 && watchlist.some(w => w.predictive_risk_level !== 'low') && (
              <div className="mb-4" data-testid="predictive-watchlist">
                <p className="text-[10px] uppercase tracking-[0.2em] text-adm-tertiary mb-3 font-['Work_Sans']">Predictive Watchlist</p>
                <div className="space-y-2">
                  {watchlist.filter(w => w.predictive_risk_level !== 'low').map(w => {
                    const colors = { watch: '#D4A843', rising: '#C87B7B', high: '#C85C5C' };
                    const color = colors[w.predictive_risk_level] || '#8A8681';
                    return (
                      <Card key={w.subscriber_id} className="bg-adm-panel border-white/[0.06]" data-testid={`watchlist-${w.subscriber_id}`}>
                        <CardContent className="p-3 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '15' }}>
                            <Activity className="w-3.5 h-3.5" style={{ color }} strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-adm-primary font-medium">{w.name}</p>
                            <p className="text-[10px] text-adm-tertiary truncate">{w.top_drivers?.join(' · ') || 'No specific drivers'}</p>
                          </div>
                          <Badge variant="outline" className="text-[9px]" style={{ borderColor: color + '30', color }}>{w.predictive_risk_level}</Badge>
                          {w.predicted_window && <span className="text-[10px] text-adm-tertiary">{w.predicted_window}</span>}
                          {w.staff_flag && <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Subscriber overview cards */}
            <div className="space-y-2" data-testid="subscriber-overview">
              {(!overview?.subscribers || overview.subscribers.length === 0) ? (
                <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-8 text-center">
                  <Brain className="w-8 h-8 text-adm-tertiary mx-auto mb-3" strokeWidth={1} />
                  <p className="text-adm-secondary text-sm">No MindFlō data yet</p>
                  <p className="text-adm-tertiary text-xs mt-1">Subscriber check-ins will appear here</p>
                </CardContent></Card>
              ) : overview.subscribers.map(sub => (
                <Card key={sub.subscriber_id}
                  className={`bg-adm-panel border-white/[0.06] cursor-pointer transition-colors ${selectedSub === sub.subscriber_id ? 'border-adm-accent/30' : 'hover:border-white/10'}`}
                  onClick={() => setSelectedSub(sub.subscriber_id)} data-testid={`sub-card-${sub.subscriber_id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (STATE_COLORS[sub.latest_state] || '#8A8681') + '15' }}>
                      <Brain className="w-4 h-4" style={{ color: STATE_COLORS[sub.latest_state] || '#8A8681' }} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-adm-primary">{sub.name}</p>
                      <p className="text-[10px] text-adm-tertiary">Last: {sub.last_checkin_date || 'No check-ins'}</p>
                    </div>
                    {sub.latest_state && (
                      <Badge variant="outline" className="text-[9px]" style={{ borderColor: (STATE_COLORS[sub.latest_state] || '') + '30', color: STATE_COLORS[sub.latest_state] }}>
                        {sub.latest_state?.replace('_', ' ')}
                      </Badge>
                    )}
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-adm-tertiary">{sub.entries_7d} entries / 7d</p>
                      <p className="text-[10px] text-adm-tertiary">{sub.completions_7d} sessions</p>
                    </div>
                    {sub.review_needed && <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Detail view */}
            {selectedSub && (
              <div className="space-y-4">
                {/* Pattern hints (shared deterministic banner) */}
                <div data-testid="mindflo-pattern-hints">
                  <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-1">Tracking Pattern Hints</p>
                  <PatternHintsBanner subscriberId={selectedSub} viewerRole={currentUser?.role} />
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {['day', 'weekly'].map(m => (
                      <button key={m} onClick={() => setViewMode(m)}
                        className={`px-3 py-1.5 rounded-full text-xs transition-colors ${viewMode === m ? 'bg-adm-accent/15 text-adm-accent' : 'text-adm-tertiary hover:text-adm-secondary'}`}
                        data-testid={`view-${m}`}>
                        {m === 'day' ? 'Day Review' : 'Weekly Review'}
                      </button>
                    ))}
                  </div>
                  {viewMode === 'day' && (
                    <Input type="date" value={reviewDate} onChange={e => setReviewDate(e.target.value)}
                      className="bg-adm-panel border-white/10 text-adm-primary h-8 text-xs w-40" data-testid="date-picker" />
                  )}
                </div>

                {/* Day review */}
                {viewMode === 'day' && dayReview && (
                  <div className="space-y-3" data-testid="day-review">
                    {dayReview.entries.length === 0 ? (
                      <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-6 text-center">
                        <p className="text-adm-secondary text-sm">No MindFlō entries for {dayReview.date}</p>
                        <p className="text-adm-tertiary text-xs mt-1">This subscriber has not completed a check-in for this date</p>
                      </CardContent></Card>
                    ) : dayReview.entries.map(e => (
                      <Card key={e.id} className="bg-adm-panel border-white/[0.06]" data-testid={`entry-${e.id}`}>
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-adm-tertiary" />
                              <span className="text-xs text-adm-tertiary">{e.entry_time}</span>
                              <Badge variant="outline" className="text-[9px]" style={{ borderColor: (STATE_COLORS[e.computed_state] || '') + '30', color: STATE_COLORS[e.computed_state] }}>
                                {e.computed_state?.replace('_', ' ')}
                              </Badge>
                            </div>
                            {e.counselor_review_needed && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[9px]">Review needed</Badge>}
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-[10px]">
                            <div><span className="text-adm-tertiary block">Phase</span><span className="text-adm-secondary">{e.current_phase?.replace('_', ' ')}</span></div>
                            <div><span className="text-adm-tertiary block">Feeling</span><span className="text-adm-secondary">{e.strongest_feeling?.replace('_', ' ')}</span></div>
                            <div><span className="text-adm-tertiary block">Trigger</span><span className="text-adm-secondary">{e.trigger_type?.replace('_', ' ')}</span></div>
                            <div><span className="text-adm-tertiary block">Need</span><span className="text-adm-secondary">{e.need_type?.replace('_', ' ')}</span></div>
                          </div>
                          {e.likely_pattern && (
                            <div className="p-2.5 rounded-lg bg-white/[0.02]">
                              <span className="text-[10px] text-adm-tertiary">Pattern: </span>
                              <span className="text-xs text-adm-accent">{dayReview.pattern_definitions?.[e.likely_pattern]?.label || e.likely_pattern}</span>
                            </div>
                          )}
                          {e.note_text && <p className="text-xs text-adm-secondary italic">"{e.note_text}"</p>}
                        </CardContent>
                      </Card>
                    ))}

                    {/* Admin note */}
                    <div className="flex gap-2">
                      <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add clinical observation..."
                        className="bg-adm-panel border-white/10 text-adm-primary text-xs flex-1 min-h-[40px]" data-testid="admin-note-input" />
                      <Button onClick={handleAddNote} disabled={savingNote || !noteText.trim()} size="sm" className="bg-adm-accent/20 text-adm-accent border-0 text-xs self-end">
                        {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add Note'}
                      </Button>
                    </div>

                    {dayReview.admin_notes?.length > 0 && (
                      <div className="space-y-1">
                        {dayReview.admin_notes.map(n => (
                          <div key={n.id} className="text-xs text-adm-secondary p-2 bg-white/[0.02] rounded-lg">
                            <span className="text-adm-tertiary">{n.note_type}: </span>{n.note_text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Weekly review */}
                {viewMode === 'weekly' && weeklyReview && (
                  <div className="space-y-4" data-testid="weekly-review">
                    <div className="grid grid-cols-4 gap-3">
                      <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-3 text-center">
                        <p className="text-lg font-['Outfit'] font-light text-adm-primary">{weeklyReview.total_entries}</p>
                        <p className="text-[9px] text-adm-tertiary uppercase">Entries</p>
                      </CardContent></Card>
                      <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-3 text-center">
                        <p className="text-lg font-['Outfit'] font-light text-adm-primary">{weeklyReview.total_completions}</p>
                        <p className="text-[9px] text-adm-tertiary uppercase">Sessions</p>
                      </CardContent></Card>
                      <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-3 text-center">
                        <p className="text-lg font-['Outfit'] font-light text-adm-primary">{weeklyReview.completion_rate}%</p>
                        <p className="text-[9px] text-adm-tertiary uppercase">Rate</p>
                      </CardContent></Card>
                      <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-3 text-center">
                        <p className="text-lg font-['Outfit'] font-light text-amber-400">{weeklyReview.unresolved_patterns?.length || 0}</p>
                        <p className="text-[9px] text-adm-tertiary uppercase">Unresolved</p>
                      </CardContent></Card>
                    </div>

                    {weeklyReview.dominant_pattern_info && (
                      <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-4">
                        <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-1">Dominant Pattern</p>
                        <p className="text-sm text-adm-accent font-medium">{weeklyReview.dominant_pattern_info.label}</p>
                        <p className="text-xs text-adm-secondary mt-1">{weeklyReview.dominant_pattern_info.description}</p>
                      </CardContent></Card>
                    )}

                    {Object.keys(weeklyReview.state_distribution || {}).length > 0 && (
                      <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-4">
                        <p className="text-[10px] uppercase tracking-wider text-adm-tertiary mb-3">State Distribution</p>
                        {Object.entries(weeklyReview.state_distribution).map(([state, count]) => (
                          <div key={state} className="flex items-center gap-3 mb-1.5">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATE_COLORS[state] || '#8A8681' }} />
                            <span className="text-xs text-adm-secondary flex-1 capitalize">{state.replace('_', ' ')}</span>
                            <span className="text-xs text-adm-tertiary font-mono">{count}</span>
                          </div>
                        ))}
                      </CardContent></Card>
                    )}

                    {weeklyReview.total_entries === 0 && (
                      <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-6 text-center">
                        <p className="text-adm-secondary text-sm">No MindFlō data for the past 7 days</p>
                      </CardContent></Card>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        ) : adminTab === 'sessions' ? (
          /* ════ SESSION LIBRARY ════ */
          <div className="space-y-4" data-testid="session-library">
            <div className="flex items-center justify-between">
              <p className="text-sm text-adm-secondary">Manage reusable session templates</p>
              <Button onClick={() => { setEditingSession(null); setShowSessionEditor(true); }} className="bg-adm-accent/20 text-adm-accent hover:bg-adm-accent/30 border-0 text-xs" data-testid="new-session-btn">
                <Plus className="w-3 h-3 mr-1" /> New Session
              </Button>
            </div>
            {sessionTemplates.map(s => (
              <Card key={s.id} className="bg-adm-panel border-white/[0.06] hover:border-white/10 transition-colors" data-testid={`session-tmpl-${s.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-9 h-9 rounded-xl bg-[#8C6B55]/10 flex items-center justify-center flex-shrink-0">
                    <Play className="w-4 h-4 text-[#8C6B55]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-adm-primary">{s.title}</p>
                    <p className="text-[10px] text-adm-tertiary truncate">{s.objective}</p>
                  </div>
                  <Badge variant="outline" className="border-white/10 text-adm-tertiary text-[9px] capitalize">{s.type?.replace('_', ' ')}</Badge>
                  <Badge variant="outline" className="border-white/10 text-adm-tertiary text-[9px]">{s.mode}</Badge>
                  <span className="text-[10px] text-adm-tertiary">{s.estimated_duration_minutes}m</span>
                  <span className="text-[10px] text-adm-tertiary">{(s.session_steps || []).length} steps</span>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-adm-tertiary hover:text-adm-accent"
                    onClick={() => { setEditingSession(s); setShowSessionEditor(true); }}>
                    <Edit3 className="w-3 h-3" />
                  </Button>
                </CardContent>
              </Card>
            ))}
            {showSessionEditor && (
              <SessionEditorModal session={editingSession} onClose={() => setShowSessionEditor(false)}
                onSaved={() => { setShowSessionEditor(false); axios.get(`${API}/mindflo/admin/session-templates`).then(r => setSessionTemplates(r.data || [])); }} />
            )}
          </div>
        ) : adminTab === 'triggers' ? (
          /* ════ TRIGGER BUILDER ════ */
          <div className="space-y-4" data-testid="trigger-builder">
            <div className="flex items-center justify-between">
              <p className="text-sm text-adm-secondary">
                {selectedSub ? `Triggers for ${overview?.subscribers?.find(s => s.subscriber_id === selectedSub)?.name || selectedSub}` : 'Select a subscriber first'}
              </p>
              {selectedSub && (
                <Button onClick={() => { setEditingTrigger(null); setShowTriggerEditor(true); }} className="bg-adm-accent/20 text-adm-accent hover:bg-adm-accent/30 border-0 text-xs" data-testid="new-trigger-btn">
                  <Plus className="w-3 h-3 mr-1" /> New Trigger
                </Button>
              )}
            </div>
            {!selectedSub ? (
              <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-8 text-center">
                <Target className="w-8 h-8 text-adm-tertiary mx-auto mb-3" strokeWidth={1} />
                <p className="text-adm-secondary text-sm">Select a subscriber from Overview tab first</p>
              </CardContent></Card>
            ) : subTriggers.length === 0 ? (
              <Card className="bg-adm-panel border-white/[0.06]"><CardContent className="p-8 text-center">
                <Target className="w-8 h-8 text-adm-tertiary mx-auto mb-3" strokeWidth={1} />
                <p className="text-adm-secondary text-sm">No triggers mapped yet</p>
                <p className="text-adm-tertiary text-xs mt-1">Create trigger profiles as patterns emerge from check-in data</p>
              </CardContent></Card>
            ) : subTriggers.map(t => (
              <Card key={t.id} className="bg-adm-panel border-white/[0.06]" data-testid={`trigger-card-${t.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-adm-primary">{t.title}</p>
                      <Badge variant="outline" className="border-white/10 text-adm-tertiary text-[9px] capitalize">{t.category}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-adm-tertiary font-mono">{t.frequency_count}x</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-adm-tertiary hover:text-adm-accent"
                        onClick={() => { setEditingTrigger(t); setShowTriggerEditor(true); }}>
                        <Edit3 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {t.description && <p className="text-xs text-adm-secondary">{t.description}</p>}
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    {t.body_response && <div><span className="text-adm-tertiary">Body: </span><span className="text-adm-secondary">{t.body_response}</span></div>}
                    {t.thought_pattern && <div><span className="text-adm-tertiary">Thought: </span><span className="text-adm-secondary">{t.thought_pattern}</span></div>}
                    {t.emotional_response && <div><span className="text-adm-tertiary">Emotion: </span><span className="text-adm-secondary">{t.emotional_response}</span></div>}
                    {t.desired_corrected_response && <div><span className="text-adm-tertiary">Corrected: </span><span className="text-emerald-400">{t.desired_corrected_response}</span></div>}
                  </div>
                </CardContent>
              </Card>
            ))}
            {showTriggerEditor && selectedSub && (
              <TriggerEditorModal trigger={editingTrigger} personId={selectedSub} onClose={() => setShowTriggerEditor(false)}
                onSaved={() => { setShowTriggerEditor(false); axios.get(`${API}/mindflo/admin/trigger-profiles/${selectedSub}`).then(r => setSubTriggers(r.data?.triggers || [])); }} />
            )}
          </div>
        ) : null}
      </div>
    </AdminLayout>
  );
}
